import { useCallback, useRef, useState } from "react";
import type { ChangeSetInfo } from "@racler/protocol";

export type Decide = (kind: "approve" | "reject", cs: ChangeSetInfo) => Promise<ChangeSetInfo>;

export interface ReviewController {
  /** ChangeSet currently under review (null = the normal editor is shown). */
  openId: string | null;
  open: (id: string) => void;
  close: () => void;
  /** Live proposals of the open chat + the approve/reject action; provided by the Agent pane. */
  changeSets: ChangeSetInfo[];
  provide: (changeSets: ChangeSetInfo[], decide: Decide) => void;
  decide: Decide;
  /** Directories whose listing changed because a ChangeSet was applied (the Explorer reloads them). */
  fsChange: { version: number; dirs: string[] };
  notifyApplied: (paths: string[]) => void;
}

const parentsOf = (p: string): string[] => {
  const parts = p.split("/").slice(0, -1);
  return ["", ...parts.map((_, i) => parts.slice(0, i + 1).join("/"))];
};

/** Lifts "which proposal is under review" above Explorer / Editor / Agent so they remain one IDE. */
export function useReview(): ReviewController {
  const [openId, setOpenId] = useState<string | null>(null);
  const [changeSets, setChangeSets] = useState<ChangeSetInfo[]>([]);
  const [fsChange, setFsChange] = useState({ version: 0, dirs: [] as string[] });
  const decideRef = useRef<Decide | null>(null);

  const provide = useCallback((cs: ChangeSetInfo[], decide: Decide) => {
    decideRef.current = decide;
    setChangeSets((prev) => (JSON.stringify(prev) === JSON.stringify(cs) ? prev : cs));
  }, []);
  const decide: Decide = useCallback((kind, cs) => {
    if (!decideRef.current) return Promise.reject(new Error("the agent pane is not available"));
    return decideRef.current(kind, cs);
  }, []);
  const notifyApplied = useCallback((paths: string[]) => {
    setFsChange((c) => ({ version: c.version + 1, dirs: [...new Set(paths.flatMap(parentsOf))] }));
  }, []);
  const close = useCallback(() => setOpenId(null), []);

  return { openId, open: setOpenId, close, changeSets, provide, decide, fsChange, notifyApplied };
}
